/* eslint-disable prefer-const */
/* eslint-disable no-continue */
/* eslint-disable no-plusplus */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/no-explicit-any */

import IconCheckWhite from '@assets/icons/check-white.svg?react';
import {
 Box,
 Button,
 Dialog,
 DialogContent,
 Theme,
 useMediaQuery,
} from '@mui/material';
import { GridFilterItem } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { memo, MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { usePage } from '../../../contexts/usePageContext';
import { setConfigStorageItem } from '../../../hooks/useConfigStorage';
import { useCurrentStructure } from '../../../hooks/useCurrentStructure';
import { changeCurrentPreset } from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { RootState } from '../../../redux/store';
import { findStructure } from '../../../utils/dataTypes';
import themeOptions from '../../../utils/theme';
import CloseButton from '../CloseButton';
import ModalConf from '../ModalConf';
import { CustomGridApi } from '../XTable/types';
import ColModalCols from './ColModalCols';
import ColModalCurrent from './ColModalCurrent';
import ColModalTabsResponsive from './ColModalTabsResponsive';

interface ColModalProps {
 isOpen: boolean;
 handleClose: React.Dispatch<React.SetStateAction<boolean>>;
 apiRef: MutableRefObject<CustomGridApi>;
}

const ColModal = memo((props: ColModalProps) => {
 const { pageName } = usePage();
 const { t } = useTranslation();
 const dispatch = useDispatch();
 const structure = useCurrentStructure();
 const { isOpen, handleClose, apiRef } = props;
 const tempColumnsPreset = useSelector(
  (state: RootState) =>
   state.clientConfiguration.tempColumnsPreset?.[pageName] || null,
 );

 const updateGrid = () => {
  if (tempColumnsPreset) {
   const { filters, columns } = tempColumnsPreset;

   const sortedFilters = {
    fields: Object.keys(
     filters?.fields || tempColumnsPreset?.filters?.fields || {},
    )
     .sort((a, b) => {
      const aIndex = columns?.findIndex((column) => column.field === a) || 0;
      const bIndex = columns?.findIndex((column) => column.field === b) || 0;
      return aIndex - bIndex;
     })
     .reduce((acc, curr) => {
      acc[curr] = filters?.fields?.[curr] ?? '';
      return acc;
     }, {} as Record<string, string | number | { [key: string]: string | number }[]>),
    operators: Object.keys(
     filters?.operators || tempColumnsPreset?.filters?.operators || {},
    )
     .sort((a, b) => {
      const aIndex = columns?.findIndex((column) => column.field === a) || 0;
      const bIndex = columns?.findIndex((column) => column.field === b) || 0;
      return aIndex - bIndex;
     })
     .reduce((acc, curr) => {
      acc[curr] = filters?.operators?.[curr] ?? '';
      return acc;
     }, {} as Record<string, string>),
   };

   const sortedPreset = {
    ...tempColumnsPreset,
    filters: sortedFilters,
   };

   dispatch(
    changeCurrentPreset({
     page: pageName,
     type: 'columns',
     preset: sortedPreset,
     temp: false,
    }),
   );
   setConfigStorageItem({
    page: pageName,
    key: 'columns',
    value: sortedPreset,
   });

   const presetFilters = sortedFilters?.fields;
   const presetOperators = sortedFilters?.operators;

   const newItems: GridFilterItem[] = [];
   if (presetFilters) {
    Object.entries(presetFilters).forEach(([field, value]) => {
     if (value === undefined || value === null || value === '') return;
     if (Array.isArray(value) && value.length === 0) return;

     const fieldStructure = findStructure(structure, field);

     if (fieldStructure?.Type === 'datetime' && typeof value === 'string') {
      const dateArray = value.split('-');
      if (
       dateArray[0] &&
       dateArray[0] !== 'x' &&
       dayjs(dateArray[0]).isValid()
      ) {
       newItems.push({
        field,
        operator: 'onOrAfter',
        value: dayjs(dateArray[0]).toDate(),
        id: `${field}-from`,
       });
      }
      if (
       dateArray[1] &&
       dateArray[1] !== 'x' &&
       dayjs(dateArray[1]).isValid()
      ) {
       newItems.push({
        field,
        operator: 'onOrBefore',
        value: dayjs(dateArray[1]).toDate(),
        id: `${field}-to`,
       });
      }
      return;
     }

     newItems.push({
      field,
      operator: presetOperators?.[field] || 'equals',
      value,
      id: `${field}-${JSON.stringify(value).replace(/[^a-zA-Z0-9]/g, '')}`,
     });
    });
   }

   apiRef.current.setFilterModel({ items: newItems }, 'upsertFilterItems');

   handleClose(false);
  }
 };

 const tabs = [
  {
   id: 'all-columns',
   label: t('colModal.colsAll'),
   content: <ColModalCols />,
  },
  {
   id: 'selected-columns',
   label: t('colModal.colsSelected'),
   content: <ColModalCurrent />,
  },
 ];

 const isLargerThanMd = useMediaQuery(themeOptions().breakpoints.up('md'));

 return (
  <Dialog
   open={isOpen}
   onClose={() => handleClose(false)}
   fullWidth
   fullScreen={!isLargerThanMd}
   sx={{
    '& .MuiDialog-container': {
     '& .MuiPaper-root': {
      width: '100%',
      height: '100%',
      maxWidth: '960px',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
     },
    },
   }}
  >
   <CloseButton closeDialog={() => handleClose(false)} />
   <DialogContent
    sx={{
     p: 0,
     flex: 1,
     minHeight: 0,
     display: 'flex',
     flexDirection: 'column',
     overflow: 'hidden',
    }}
   >
    <Box
     sx={{
      display: 'flex',
      flex: 1,
      minHeight: 0,
      flexDirection: { xs: 'column', md: 'row' },
      fontSize: 14,
      overflow: 'hidden',
     }}
    >
     <Box sx={{ flexShrink: 0 }}>
      <ModalConf />
     </Box>
     <Box
      sx={{
       borderLeft: (theme: Theme) => ({
        xs: 'none',
        md: `1px solid ${theme.palette.kemek.lightestGray}`,
       }),
       display: 'flex',
       flexDirection: 'column',
       flex: 1,
       minHeight: 0,
       px: { xs: 0, md: 3 },
       pb: { xs: 0, md: 0 },
       pt: { xs: 0, md: '67px' },
       width: '100%',
       overflow: 'hidden',
      }}
     >
      <Box
       sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
       }}
      >
       <Box
        sx={{
         display: { xs: 'none', md: 'flex' },
         gap: 3,
         flex: 1,
         minHeight: 0,
         alignItems: 'stretch',
        }}
       >
        <ColModalCols />
        <ColModalCurrent />
       </Box>

       <Box
        sx={{
         display: { xs: 'flex', md: 'none' },
         flexDirection: 'column',
         flex: 1,
         minHeight: 0,
         overflow: 'hidden',
        }}
       >
        <ColModalTabsResponsive
         tabs={tabs}
         defaultTab={0}
         variant="fullWidth"
         ariaLabel={t('colModal.colSettings')}
         wrapSx={{
          flex: 1,
          minHeight: 0,
          height: 'auto',
         }}
         sx={{ flexShrink: 0 }}
         panelSx={{
          flex: 1,
          minHeight: 0,
          py: 0,
          px: 0,
          overflow: 'hidden',
         }}
        />
       </Box>

       <Box
        sx={{
         flexShrink: 0,
         display: 'flex',
         justifyContent: 'flex-end',
         px: { xs: 3, md: 1 },
         py: { xs: 3, md: 3 },
         gap: 1,
        }}
       >
        <Button
         onClick={() => handleClose(false)}
         variant="text"
         color="secondary"
         size="large"
        >
         {t('global.cancel')}
        </Button>
        <Button
         variant="contained"
         endIcon={<IconCheckWhite style={{ width: 16, height: 11 }} />}
         onClick={() => {
          updateGrid();
         }}
         size="large"
        >
         {t('global.apply')}
        </Button>
       </Box>
      </Box>
     </Box>
    </Box>
   </DialogContent>
  </Dialog>
 );
});
ColModal.displayName = 'ColModal';
export default ColModal;
